import React, { useRef, useCallback } from "react";
import { useClickOutsideEffect } from "utility/useClickOutside";
import { useMaxHeightTransition } from "utility/useMaxHeightTransition";

type Option = { id: string; display: string; val?: any };

interface Props {
  className?: string;
  menuClassName?: string;
  options: Option[];
  selectedID?: string;
  placeholder?: string;
  flipped?: boolean;
  onSelect: (selected: Option) => void;
  onAddNew?: () => void;
}

const Dropdown: React.FC<Props> = ({
  className,
  menuClassName,
  options,
  selectedID,
  placeholder,
  onSelect,
  flipped,
  onAddNew,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { open, setOpen, style } = useMaxHeightTransition("0", "240px");
  const handleClickOutside = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  useClickOutsideEffect(ref, handleClickOutside);

  const selected = options.find((o) => o.id === selectedID);
  const display = selected ? selected.display : placeholder ? placeholder : "";

  return (
    <div className={`relative`} ref={ref}>
      <div
        className={`${className ? className : ""} flex items-center cursor-pointer`}
        onClick={() => setOpen(!open)}
      >
        <span className="flex-grow truncate">{display}</span>
        <svg
          className="fill-current h-4 w-4 no-print"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
        </svg>
      </div>

      {open && (
        <div
          className={`rounded border border-gray-200 shadow-lg overflow-y-scroll absolute left-0 overflow-auto z-50 bg-white
            ${flipped ? "bottom-0 mb-10" : "top-0 mt-10"}
            ${menuClassName ? menuClassName : ""}
          `}
          style={style}
        >
          {options.length > 0 ? (
            options.map((option) => (
              <div
                className={`py-2 px-4 cursor-pointer hover:bg-gray-100`}
                key={option.id}
                onClick={() => {
                  onSelect(option);
                  setOpen(false);
                }}
              >
                {option.display}
              </div>
            ))
          ) : (
            <div className={`py-2 px-4`}>no options...</div>
          )}
          {onAddNew && (
            <button
              onClick={() => {
                onAddNew();
                setOpen(false);
              }}
              className={`py-2 w-full hover:bg-gray-100 focus:outline-none border-t border-gray-200`}
            >
              Add new
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
